/**
 * Personal UI preferences (D-123: per user, `GET/PUT /preferences`, no PIN, not audited) and their
 * effect on the document, exactly as the old app applied them:
 *   - theme: a `dark` class on <html> (original/src/App.tsx:4329-4331; `@custom-variant dark` in the ui stylesheet),
 *   - language: `dir`/`lang` on <html> (the screens also set `dir` on their root, App.tsx:25294),
 *   - display scale: `document.documentElement.style.zoom` (App.tsx:1901).
 * Before login the login screen uses the viewer's last theme / language from localStorage (a per-viewer
 * convenience only; the account's values arrive with the session).
 */
import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Locale } from "@faylasuf/i18n";
import { applyThemeToDocument, themeFromPreference, type CardSize, type CustomAppTheme, type IconSize } from "@faylasuf/ui";
import type { DataSource, PreferencesMap } from "../api/types.js";
import { keys } from "./query.js";

export interface UiPrefs {
  readonly theme: "light" | "dark";
  readonly language: Locale;
  readonly displayScale: number;
  readonly iconSize: IconSize;
  readonly cardSize: CardSize;
  readonly accent: string;
  /** [C30] D-285: the Theme Studio theme (`ui.theme_studio`); the old default is the first preset (theme.ts:184). */
  readonly themeStudio: CustomAppTheme;
  readonly icons: Readonly<Record<string, string>>;
  readonly sidebarOrder: readonly string[] | null;
  readonly settingsOrder: readonly string[] | null;
  readonly dashboardOrder: readonly string[] | null;
  readonly dashboardHidden: readonly string[];
}

/** Defaults of packages/contracts USER_PREFERENCES (D-123); THEME_COLORS[0] is the old default colour (App.tsx:1879). */
export const DEFAULT_PREFS: UiPrefs = {
  theme: "light",
  language: "ar",
  displayScale: 100,
  iconSize: "md",
  cardSize: "md",
  accent: "#2563eb",
  themeStudio: themeFromPreference(null),
  icons: {},
  sidebarOrder: null,
  settingsOrder: null,
  dashboardOrder: null,
  dashboardHidden: [],
};

const LS_THEME = "faylasuf.ui.theme";
const LS_LANG = "faylasuf.ui.language";

export function readViewerPrefs(): Partial<UiPrefs> {
  try {
    const theme = localStorage.getItem(LS_THEME);
    const lang = localStorage.getItem(LS_LANG);
    return {
      ...(theme === "dark" || theme === "light" ? { theme } : {}),
      ...(lang === "ar" || lang === "en" ? { language: lang } : {}),
    };
  } catch {
    return {};
  }
}

export function rememberViewerPrefs(p: Pick<UiPrefs, "theme" | "language">): void {
  try {
    localStorage.setItem(LS_THEME, p.theme);
    localStorage.setItem(LS_LANG, p.language);
  } catch {
    /* storage unavailable: the login screen simply uses the defaults */
  }
}

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");

export function prefsFromMap(map: PreferencesMap | undefined, base: UiPrefs): UiPrefs {
  if (!map) return base;
  const theme = map["ui.theme"];
  const language = map["ui.language"];
  const scale = map["ui.display_scale"];
  const iconSize = map["ui.icon_size"];
  const cardSize = map["ui.card_size"];
  const accent = map["ui.accent"];
  const icons = map["ui.icons"];
  return {
    theme: theme === "dark" || theme === "light" ? theme : base.theme,
    language: language === "ar" || language === "en" ? language : base.language,
    displayScale: typeof scale === "number" && Number.isFinite(scale) ? scale : base.displayScale,
    iconSize: iconSize === "sm" || iconSize === "md" || iconSize === "lg" || iconSize === "xl" ? iconSize : base.iconSize,
    cardSize: cardSize === "sm" || cardSize === "md" || cardSize === "lg" ? cardSize : base.cardSize,
    accent: typeof accent === "string" && /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : base.accent,
    themeStudio: map["ui.theme_studio"] !== undefined ? themeFromPreference(map["ui.theme_studio"]) : base.themeStudio,
    icons: icons && typeof icons === "object" && !Array.isArray(icons) ? (icons as Record<string, string>) : base.icons,
    sidebarOrder: isStringArray(map["ui.sidebar_order"]) ? map["ui.sidebar_order"] : base.sidebarOrder,
    settingsOrder: isStringArray(map["ui.settings_order"]) ? map["ui.settings_order"] : base.settingsOrder,
    dashboardOrder: isStringArray(map["ui.dashboard_order"]) ? map["ui.dashboard_order"] : base.dashboardOrder,
    dashboardHidden: isStringArray(map["ui.dashboard_hidden"]) ? map["ui.dashboard_hidden"] : base.dashboardHidden,
  };
}

/** Applies theme, direction, language and zoom to the document (see header comment). */
export function useDocumentEffects(prefs: Pick<UiPrefs, "theme" | "language" | "displayScale" | "themeStudio">): void {
  // [C30] D-285: the Theme Studio colours on <html> exactly as App.tsx:1914-1916 (CSSOM custom properties, CSP-safe)
  useEffect(() => {
    applyThemeToDocument(prefs.themeStudio);
  }, [prefs.themeStudio]);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", prefs.theme === "dark");
    root.lang = prefs.language;
    root.dir = prefs.language === "ar" ? "rtl" : "ltr";
    // `zoom` is non-standard but is what the old app used; typed through the style map
    (root.style as CSSStyleDeclaration & { zoom: string }).zoom = prefs.displayScale === 100 ? "" : `${prefs.displayScale}%`;
    rememberViewerPrefs(prefs);
  }, [prefs.theme, prefs.language, prefs.displayScale]);
}

export interface PreferencesApi {
  readonly prefs: UiPrefs;
  readonly loaded: boolean;
  /** Optimistic `PUT /preferences`; the cache is rolled back and `onError` called on refusal. */
  readonly set: (key: string, value: unknown, onError?: (e: unknown) => void) => void;
}

export function usePreferences(source: DataSource, enabled: boolean, base: UiPrefs): PreferencesApi {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: keys.preferences, queryFn: () => source.preferences(), enabled });
  const prefs = useMemo(() => prefsFromMap(enabled ? query.data : undefined, base), [enabled, query.data, base]);

  const mutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => source.setPreference(key, value),
    onMutate: async ({ key, value }) => {
      const previous = qc.getQueryData<PreferencesMap>(keys.preferences);
      qc.setQueryData<PreferencesMap>(keys.preferences, { ...(previous ?? {}), [key]: value });
      return { previous };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.preferences, ctx.previous);
    },
  });

  const set = useCallback(
    (key: string, value: unknown, onError?: (e: unknown) => void) => {
      mutation.mutate({ key, value }, { onError: (e) => onError?.(e) });
    },
    [mutation],
  );

  return { prefs, loaded: !enabled || query.isSuccess, set };
}
